import { type ChangeEvent } from "react";
import {
  Text,
  Pagination,
  Loader,
  Box,
  Select,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrlPartnerPageSuperTeams } from "@fwa/src/services/partnerService";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { queryAsString } from "@fwa/src/utils/urlUtils";

import MemberSuperTeamDonate from "../MemberSuperTeamDonate";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  FwsResponseData,
  SuperTeamMemberSortType,
  SuperTeamPageType,
} from "@fwa/src/types";

export function PartnerPageSuperTeams({
  partnerPageId,
  itemsPerPage,
}: {
  partnerPageId: string;
  itemsPerPage: number;
}) {
  const router = useRouter();
  const { trackError } = useTracking();
  const { superteam_pages_page: superTeamPagesPage, sort: sortQuery } =
    router?.query || {};
  const currentPage = parseInt(queryAsString(superTeamPagesPage) || "1", 10);
  const sort = queryAsString(sortQuery || "amount") as SuperTeamMemberSortType;
  const url = partnerPageId
    ? fwsUrlPartnerPageSuperTeams({
        partnerPageId,
        currentPage,
        itemsPerPage,
        sort,
      })
    : null;
  const { data, error, totalItems } = useSwrGet<
    FwsResponseData<SuperTeamPageType>
  >(url, {});
  const superTeams = data?.results || [];

  // We are effectively storing page number in the query string
  const setPageInQueryString = (pageNumber: number) => {
    // pathname isn't going to cut the mustard because it's on the teams page with a [slug]
    // so we need to make the url look like how it should do which is annoying because this list should be
    // agnostic to the page path however it still needs to know how to amend the query string in the URL
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, superteam_pages_page: pageNumber },
        },
        undefined,
        { shallow: true },
      )
      .catch((e: Error) => {
        trackError(e, {
          component: "PartnerPageSuperTeams",
          function: "setPageInQueryString",
        });
      });
  };

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.push(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((e: Error) => {
        trackError(e, {
          component: "PartnerPageSuperTeams",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as SuperTeamMemberSortType;
    setSortInQueryString(sortValue || null);
  };

  return (
    <>
      {/* Error State */}
      {error ? (
        <Text as="span">Unable to get partner superteam pages</Text>
      ) : null}

      {/* Loading State */}
      {!error && !data ? <Loader /> : null}

      {/* Empty State */}
      {!error && data && !superTeams?.length ? (
        <Text as="span">
          There are currently no superteam pages for this partner
        </Text>
      ) : null}

      {/* Data State */}
      {data && superTeams?.length ? (
        <>
          <Box>
            <Select
              required
              value={sort}
              label="Sort members by"
              onChange={handleFilterChange}
              aria-controls="memberList"
              hideRequiredIndicationInLabel
            >
              <option value="alpha">A-Z</option>
              <option value="amount">amount raised</option>
              <option value="recent">recently joined</option>
            </Select>
          </Box>
          <Box>
            <ul
              className={sharedStyles["member-list"]}
              id="memberList"
              aria-live="assertive"
            >
              {!error &&
                superTeams.map((superTeamPage: SuperTeamPageType) => (
                  <li key={superTeamPage.uniqueId}>
                    <MemberSuperTeamDonate superTeamPage={superTeamPage} />
                  </li>
                ))}
            </ul>
          </Box>
          <Box>
            <div className={sharedStyles["row-space-around"]}>
              <Text>{`Showing ${superTeams.length} of ${totalItems} members`}</Text>
            </div>
          </Box>
          <Pagination
            aria-controls="memberList"
            current={currentPage}
            perPage={itemsPerPage}
            items={totalItems}
            hideLast={false}
            pagerCallback={(n: number) => {
              setPageInQueryString(n);
            }}
          />
        </>
      ) : null}
    </>
  );
}

export default PartnerPageSuperTeams;
